'use client'

import * as React from 'react'
import { useQuery } from '@tanstack/react-query'
import type { GenerateMcpTokenResponse } from '~/app/api/mcp/generate-token/_schema'
import { useUserStore } from '~/app/_store/user-store'
import { handleCopyPkCloak, mcpTokenCloack as mcpTokenCloak } from './pk-cloak'
import { BaseHubImage } from 'basehub/next-image'
import { ButtonLink } from '~/sotano-kit/primitives/button'
import { useTheme } from 'next-themes'
import { Skeleton } from '~/sotano-kit/primitives/skeleton'
import {
  CodeSnippet,
  CodeSnippetActions,
  CodeSnippetContainer,
} from '~/sotano-kit/primitives/code-snippet'
import { MCPIcon } from '~/components/icons/ai'
interface McpTokenDisplayProps {
  repoId: string
  repoSlug: string
  teamSlug: string | undefined
  integration: 'cursor' | 'other'
}

const MCP_URL = 'https://basehub.com/api/mcp'
const CURSOR_IMAGES = {
  dark: 'https://cursor.com/deeplink/mcp-install-dark.svg',
  light: 'https://cursor.com/deeplink/mcp-install-light.svg',
}

function McpTokenContent({
  repoId,
  repoSlug,
  teamSlug,
  integration,
}: {
  repoId: string
  repoSlug: string
  teamSlug: string | undefined
  integration: 'cursor' | 'other'
}) {
  const userId = useUserStore((s) => s.optimisticUser?.id)
  const { theme } = useTheme()
  const {
    data: tokenResponse,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['mcp-token', teamSlug, repoId, userId],
    queryFn: async (): Promise<GenerateMcpTokenResponse> => {
      const response = await fetch('/api/mcp/generate-token', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ repoId, teamSlug }),
      })

      if (!response.ok) {
        throw new Error(`${response.status} ${response.statusText}`)
      }

      return response.json() as Promise<GenerateMcpTokenResponse>
    },
    staleTime: 1000 * 60 * 60, // 1 hour - tokens don't change often
    cacheTime: 1000 * 60 * 60 * 24, // 24 hours
    enabled: Boolean(userId && teamSlug),
  })

  React.useEffect(() => {
    const cursorImage = new Image()
    cursorImage.src = CURSOR_IMAGES[theme as keyof typeof CURSOR_IMAGES]
  }, [theme])

  if (isLoading) {
    return <McpTokenSkeleton />
  }

  if (error || !tokenResponse?.success) {
    const errorMessage =
      error instanceof Error
        ? error.message
        : tokenResponse && !tokenResponse.success
          ? tokenResponse.error
          : 'Unknown error'
    return (
      <div className="text-dark-error text-xs max-w-[200px] leading-[1.1]">
        Failed to generate MCP token: {errorMessage}
      </div>
    )
  }

  const token = tokenResponse.data.mcpToken

  return (
    <Instructions
      integration={integration}
      token={token}
      repoSlug={repoSlug.slice(0, 24)}
    />
  )
}

function Instructions({
  integration,
  token,
  repoSlug,
}: {
  integration: 'cursor' | 'other' | 'claude-code'
  token: string
  repoSlug: string
}) {
  // Create MCP configuration with the token
  const mcpConfig = React.useMemo(
    () => ({
      url: MCP_URL,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }),
    [token]
  )

  // Base64 encode the configuration using Buffer
  const encodedConfig = Buffer.from(JSON.stringify(mcpConfig)).toString(
    'base64'
  )

  const mcpName = `basehub_${repoSlug}`

  // Open Cursor with the token-enabled configuration
  const cursorInstallUrl = `cursor://anysphere.cursor-deeplink/mcp/install?name=${mcpName}&config=${encodedConfig}`

  const instructions = React.useMemo(() => {
    const claudeLine = `claude mcp add --transport http ${mcpName} ${MCP_URL} --header "Authorization: Bearer ${token}"`
    switch (integration) {
      case 'cursor':
        return (
          <ButtonLink
            unstyled
            focusStyled
            href={cursorInstallUrl}
            target="_blank"
            rel="noreferrer"
            className="block h-8 w-[126px] overflow-hidden"
          >
            <BaseHubImage
              className="block dark:hidden h-8 w-[126px] object-contain"
              src="https://cursor.com/deeplink/mcp-install-dark.svg"
              alt="Add basehub MCP server to Cursor"
              height="32"
              width="126"
            />
            <BaseHubImage
              className="hidden dark:block h-8 w-[126px] object-contain"
              src="https://cursor.com/deeplink/mcp-install-light.svg"
              alt="Add basehub MCP server to Cursor"
              height="32"
              width="126"
            />
          </ButtonLink>
        )
      case 'claude-code':
        return (
          <CodeSnippetContainer className="w-full">
            <CodeSnippet
              code={claudeLine.replace(token, mcpTokenCloak)}
              copyContentOverride={claudeLine}
              onCopy={(e) => handleCopyPkCloak(e, token, mcpTokenCloak)}
              language="bash"
              // lineNumbers
              className="p-4 light:bg-dark-base"
              unstyled
            />
          </CodeSnippetContainer>
        )
      case 'other':
        return (
          <CodeSnippetContainer className="w-full">
            <CodeSnippetActions label="mcp.json" icon={<MCPIcon />} />
            <CodeSnippet
              code={JSON.stringify({ mcpName: mcpConfig }, null, 2).replace(
                token,
                mcpTokenCloak
              )}
              copyContentOverride={JSON.stringify(mcpConfig, null, 2)}
              onCopy={(e) => handleCopyPkCloak(e, token, mcpTokenCloak)}
              language="json"
              lineNumbers
              className="p-4 light:bg-dark-base"
              unstyled
            />
          </CodeSnippetContainer>
        )
    }
  }, [mcpName, token, integration, cursorInstallUrl, mcpConfig])

  return (
    <div className="space-y-4">
      {/* Integration Selection */}
      <div className="flex items-center justify-between gap-2">
        {instructions}
      </div>
    </div>
  )
}

function McpTokenSkeleton() {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <Skeleton height={32} width={80} />
      </div>
    </div>
  )
}

export function McpTokenDisplay({
  repoId,
  repoSlug,
  teamSlug,
  integration,
}: McpTokenDisplayProps) {
  return (
    <React.Suspense fallback={<McpTokenSkeleton />}>
      <McpTokenContent
        repoId={repoId}
        repoSlug={repoSlug}
        teamSlug={teamSlug}
        integration={integration}
      />
    </React.Suspense>
  )
}
